import React from 'react'

/**
 * Read-only display of the picked weld, PQR No., and Rev. surfaced in
 * the PQR Preview page header. Editing PQR No. / Rev. continues to
 * happen via `IdentifiersModal` on the canvas; because the pills are
 * driven by the staged snapshot, they update live as the user edits.
 *
 * Added in `pqr-report-history-and-rev` slice 05 alongside the
 * removal of the "Change Weld" button from the toolbar.
 *
 * Polish-pass slice 06 refinements:
 *  - Weld + PQR No. render value-only (the page header already
 *    establishes context); only Rev. keeps a label because a bare
 *    integer is too ambiguous.
 *  - Each pill carries a different CRCE-token tint family so the eye
 *    can pick them out at a glance:
 *      Weld     -> blueprint-blue   (identity)
 *      PQR No.  -> galvanizing-green (record)
 *      Rev      -> amber             (version)
 *  - The container stays `flex flex-wrap` so the pills sit inline
 *    inside the existing PageHeader subtitle row and only wrap when
 *    the viewport is narrow.
 */
export interface PqrPreviewHeaderPillsProps {
  /** weldNumber from `floorsheet.welds[i]`. Empty when none assigned. */
  weldNumber?: string
  /** The picked weldId. Absent when in legacy aggregated mode. */
  weldId?: string
  /** Multi-weld: one pill per scope (weld number only, pick order). */
  weldScopes?: Array<{ weldNumber?: string }>
  pqrNumber: string
  revision: string
}

const EM_DASH = '\u2014'

type PillTone = 'weld' | 'pqr' | 'rev'

const TONE_CLASSES: Record<PillTone, string> = {
  weld: 'bg-blueprint-blue/10 text-blueprint-blue border border-blueprint-blue/20',
  pqr: 'bg-galvanizing-green/10 text-galvanizing-green border border-galvanizing-green/20',
  rev: 'bg-amber-500/10 text-amber-700 border border-amber-500/30'
}

function Pill({
  tone,
  testId,
  label,
  value
}: {
  tone: PillTone
  testId: string
  label?: string
  value: string
}) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full px-1.5 py-0.5 text-[9px] font-medium ${TONE_CLASSES[tone]}`}
    >
      {label ? (
        <span className="font-semibold uppercase tracking-wide">{label}:</span>
      ) : null}
      <span>{value}</span>
    </span>
  )
}

function weldPillValue(weldNumber?: string): string {
  const trimmed = (weldNumber || '').trim()
  return trimmed || EM_DASH
}

export default function PqrPreviewHeaderPills({
  weldNumber,
  weldId,
  weldScopes,
  pqrNumber,
  revision
}: PqrPreviewHeaderPillsProps) {
  const multiScopes =
    Array.isArray(weldScopes) && weldScopes.length > 1 ? weldScopes : null

  const singleWeldValue = multiScopes
    ? null
    : weldPillValue(weldNumber ?? (weldId ? undefined : undefined))

  return (
    <div
      data-testid="pqr-preview-header-pills"
      className="flex flex-wrap items-center gap-1.5"
    >
      {multiScopes ? (
        multiScopes.map((scope, idx) => (
          <Pill
            key={idx}
            tone="weld"
            testId={idx === 0 ? 'pqr-pill-weld' : `pqr-pill-weld-${idx}`}
            value={weldPillValue(scope.weldNumber)}
          />
        ))
      ) : (
        <Pill tone="weld" testId="pqr-pill-weld" value={singleWeldValue ?? EM_DASH} />
      )}
      <Pill
        tone="pqr"
        testId="pqr-pill-pqr-number"
        value={pqrNumber.trim() ? pqrNumber : EM_DASH}
      />
      <Pill
        tone="rev"
        testId="pqr-pill-rev"
        label="Rev"
        value={revision.trim() ? revision : EM_DASH}
      />
    </div>
  )
}
